import { XIcon } from 'lucide-react';
import { Flex } from '@/components/Flex';
import { Form } from '@/components/Form';
import { Icon } from '@/components/Icon';
import { Input } from '@/components/Input';
import { Section } from '@/components/Section';
import { Select } from '@/components/Select';
import * as shared from '../../tasks.css';
import * as styles from './sections.css';
import type { ReportingTaskCrossTabConfig } from './types';

type CrossTabulationSettingsProps = {
    taskTypeId: string;
    config: ReportingTaskCrossTabConfig | null;
};

export function CrossTabulationSettings({ taskTypeId, config }: CrossTabulationSettingsProps) {
    const isSurveyTask = taskTypeId === 'NEW_SURVEY' || taskTypeId === 'OVERWRITE_SURVEY';

    if (isSurveyTask || config === null) {
        return null;
    }

    return (
        <Section>
            <Flex direction="column" gap="5">
                <Form.Row>
                    <Form.Field
                        label="属性別に出力する"
                        control={(props) => (
                            <Select {...props} defaultValue={config.separateByAttr ?? ''} disabled>
                                {config.separateByAttr ? (
                                    <option value={config.separateByAttr}>{config.separateByAttr}</option>
                                ) : (
                                    <option value="">出力しない</option>
                                )}
                            </Select>
                        )}
                    />
                    <Form.Field
                        label="コメントの行"
                        control={(props) => (
                            <Input
                                {...props}
                                type="text"
                                defaultValue={config.commentFields?.join(',') ?? ''}
                                placeholder="フリーテキストの列は自動的に除外される"
                                disabled
                            />
                        )}
                    />
                </Form.Row>

                {config.crossConditions && config.crossConditions.length > 0 && (
                    <>
                        <hr className={shared.divider} />
                        <Flex direction="column" gap="6">
                            <span className={shared.sectionLabel}>集計条件</span>
                            <Flex direction="column" gap="3">
                                {config.crossConditions.map((condition, index) => (
                                    // biome-ignore lint/suspicious/noArrayIndexKey: disabled display-only fields
                                    <Flex key={index} align="end" gap="2">
                                        <span className={shared.conditionIndex}>{index + 1}</span>

                                        <div className={styles.conditionGroup}>
                                            <Form.Field
                                                label="選択属性1"
                                                control={(props) => (
                                                    <Select {...props} defaultValue={condition[0]} disabled>
                                                        <option value={condition[0]}>{condition[0]}</option>
                                                    </Select>
                                                )}
                                            />
                                            <span className={shared.conditionSeparator}>
                                                <Icon icon={XIcon} />
                                            </span>
                                            <Form.Field
                                                label="選択属性2"
                                                control={(props) => (
                                                    <Select {...props} defaultValue={condition[1]} disabled>
                                                        <option value={condition[1]}>{condition[1]}</option>
                                                    </Select>
                                                )}
                                            />
                                            <span className={shared.conditionSeparator}>
                                                <Icon icon={XIcon} />
                                            </span>
                                            <Form.Field
                                                label="選択属性3"
                                                control={(props) => (
                                                    <Select {...props} defaultValue={condition[2]} disabled>
                                                        <option value={condition[2]}>{condition[2]}</option>
                                                    </Select>
                                                )}
                                            />
                                        </div>
                                    </Flex>
                                ))}
                            </Flex>
                        </Flex>
                    </>
                )}
            </Flex>
        </Section>
    );
}

/**
 * Read-only cross-tabulation settings. Survey (new / overwrite) tasks can't be
 * cross-tabulated, so the card is hidden entirely for them.
 */
export function CrossTabulationSettingsSkeleton() {
    return (
        <Section>
            <Flex direction="column" gap="5">
                <Form.Row>
                    <Form.FieldSkeleton />
                    <Form.FieldSkeleton />
                </Form.Row>
            </Flex>
        </Section>
    );
}
